// Apolo IDE — SpecReviewPanel (S3 Epic E3a).
//
// Renders an InlineSpec for human review before the turn proceeds.
// Artifact pattern mirrors PlanPreview.jsx — same collapsible sections,
// same rx-* styling, same a11y idioms (aria-expanded / aria-controls).
//
// Props:
//   spec     {InlineSpec}  — the spec to display/edit
//   onAccept {(spec) => void} — called with the (possibly edited) spec
//   onEdit   {() => void}  — optional external signal when edit mode opens
//   onSkip   {() => void}  — called immediately on Pular (1 click, no confirm)
//   busy     {boolean}     — disables all actions when true

import { useState, useId, useRef, useEffect } from 'react';
import { Check, Pencil, SkipForward, ChevronDown, ChevronRight, FileText, X } from 'lucide-react';
import { sanitizeSpec } from '../../../lib/specGate.js';

// ── collapsible section ───────────────────────────────────────────────────

function SpecSection({ title, id, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = `${id}-body`;
  return (
    <div className="rounded-lg border border-apolo-borderSubtle bg-apolo-surface1">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs font-medium text-apolo-textPrimary"
      >
        <span>{title}</span>
        {open ? (
          <ChevronDown size={13} className="text-apolo-textTertiary" aria-hidden="true" />
        ) : (
          <ChevronRight size={13} className="text-apolo-textTertiary" aria-hidden="true" />
        )}
      </button>
      <div id={bodyId} hidden={!open} className="border-t border-apolo-borderSubtle px-3 py-2">
        {children}
      </div>
    </div>
  );
}

// ── main component ────────────────────────────────────────────────────────

const noop = () => {};

export default function SpecReviewPanel({
  spec,
  onAccept = noop,
  onEdit = noop,
  onSkip = noop,
  busy = false,
}) {
  const uid = useId();
  const [editMode, setEditMode] = useState(false);
  // M2 — disable the action buttons the instant Accept/Skip is clicked, before the
  // parent re-renders the resolved state. Defensive against a double-click firing
  // two resume turns (the synchronous ref-null guard in SessionWorkspace is the
  // real correctness anchor; this just closes the within-frame visual gap).
  const [acted, setActed] = useState(false);
  const isBusy = busy || acted;
  const [editedDesign, setEditedDesign] = useState(spec?.design ?? '');
  const [editedRequirements, setEditedRequirements] = useState(
    (spec?.requirements ?? []).join('\n')
  );
  const [editedAcceptance, setEditedAcceptance] = useState(
    (spec?.acceptance ?? []).join('\n')
  );

  // M2 — focus the first (design) textarea when entering edit mode
  const firstTextareaRef = useRef(null);
  useEffect(() => {
    if (editMode && firstTextareaRef.current) {
      firstTextareaRef.current.focus();
    }
  }, [editMode]);

  function handleEditToggle() {
    setEditMode(true);
    onEdit();
  }

  // F5 — leave edit mode WITHOUT committing or discarding the spec. The edited
  // textareas keep their state (they are not reset), so re-entering edit resumes
  // where the user left off; the read-only view shows the ORIGINAL spec until
  // Accept commits. This makes Edit reversible (previously the only exits were
  // Accept/Skip, both destructive of the read-only intent).
  function handleCancelEdit() {
    if (isBusy) return;
    setEditMode(false);
  }

  function handleAccept() {
    if (isBusy) return;
    setActed(true);
    if (editMode) {
      const raw = {
        id: spec?.id,
        design: editedDesign,
        requirements: editedRequirements.split('\n').map((s) => s.trim()).filter(Boolean),
        acceptance: editedAcceptance.split('\n').map((s) => s.trim()).filter(Boolean),
      };
      const sanitized = sanitizeSpec(raw);
      // M4 — if sanitizeSpec returns null (all fields cleared), treat as skip
      if (sanitized === null) {
        onSkip();
      } else {
        onAccept(sanitized);
      }
    } else {
      onAccept(spec);
    }
  }

  function handleSkip() {
    if (isBusy) return;
    setActed(true);
    onSkip();
  }

  return (
    <section
      role="group"
      aria-label="Especificação do turno"
      data-testid="spec-review-panel"
      className="space-y-2"
    >
      {/* Header */}
      <header className="flex items-center gap-2 text-xs font-medium text-apolo-textPrimary">
        <FileText size={12} className="text-apolo-accent" aria-hidden="true" />
        <span>Spec do turno</span>
      </header>

      {/* Design section */}
      <SpecSection title="Design" id={`${uid}-design`} defaultOpen>
        <div data-testid="spec-section-design">
          {editMode ? (
            <textarea
              ref={firstTextareaRef}
              data-testid="spec-edit-design"
              aria-label="Editar design da especificação"
              rows={5}
              value={editedDesign}
              onChange={(e) => setEditedDesign(e.target.value)}
              className="w-full resize-y rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2 py-1.5 font-mono text-[11px] text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent"
            />
          ) : (
            <p className="whitespace-pre-wrap text-[11px] text-apolo-textSecondary">
              {spec?.design || <span className="italic text-apolo-textTertiary">Sem design.</span>}
            </p>
          )}
        </div>
      </SpecSection>

      {/* Requirements section */}
      <SpecSection title="Requisitos" id={`${uid}-requirements`} defaultOpen>
        <div data-testid="spec-section-requirements">
          {editMode ? (
            <textarea
              data-testid="spec-edit-requirements"
              aria-label="Editar requisitos (um por linha)"
              rows={4}
              value={editedRequirements}
              onChange={(e) => setEditedRequirements(e.target.value)}
              className="w-full resize-y rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2 py-1.5 font-mono text-[11px] text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent"
            />
          ) : (
            <ol className="space-y-1 pl-4 text-[11px] text-apolo-textSecondary list-decimal">
              {(spec?.requirements ?? []).map((req, i) => (
                 
                <li key={i}>{req}</li>
              ))}
              {(spec?.requirements ?? []).length === 0 && (
                <li className="list-none pl-0 italic text-apolo-textTertiary">Sem requisitos.</li>
              )}
            </ol>
          )}
        </div>
      </SpecSection>

      {/* Acceptance section */}
      <SpecSection title="Critérios de aceitação" id={`${uid}-acceptance`} defaultOpen>
        <div data-testid="spec-section-acceptance">
          {editMode ? (
            <textarea
              data-testid="spec-edit-acceptance"
              aria-label="Editar critérios de aceitação (um por linha)"
              rows={4}
              value={editedAcceptance}
              onChange={(e) => setEditedAcceptance(e.target.value)}
              className="w-full resize-y rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2 py-1.5 font-mono text-[11px] text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent"
            />
          ) : (
            <ul className="space-y-1 text-[11px] text-apolo-textSecondary" role="list">
              {(spec?.acceptance ?? []).map((crit, i) => (
                 
                <li key={i} className="flex items-start gap-1.5">
                  <span
                    className="mt-0.5 h-3 w-3 shrink-0 rounded border border-apolo-borderSubtle"
                    aria-hidden="true"
                  />
                  {crit}
                </li>
              ))}
              {(spec?.acceptance ?? []).length === 0 && (
                <li className="italic text-apolo-textTertiary">Sem critérios.</li>
              )}
            </ul>
          )}
        </div>
      </SpecSection>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 border-t border-apolo-borderSubtle pt-2">
        {/* Pular — 1 click, always visible */}
        <button
          type="button"
          data-testid="spec-skip-btn"
          aria-label="Pular especificação"
          disabled={isBusy}
          onClick={handleSkip}
          className="rx-btn"
        >
          <SkipForward size={11} aria-hidden="true" />
          Pular
        </button>

        {/* Editar */}
        {!editMode && (
          <button
            type="button"
            data-testid="spec-edit-btn"
            aria-label="Editar especificação"
            disabled={isBusy}
            onClick={handleEditToggle}
            className="rx-btn"
          >
            <Pencil size={11} aria-hidden="true" />
            Editar
          </button>
        )}

        {/* F5 — Cancelar edição: reversible exit from edit mode (no commit, no
            discard). Only shown while editing, where "Editar" is hidden. */}
        {editMode && (
          <button
            type="button"
            data-testid="spec-cancel-edit-btn"
            aria-label="Cancelar edição da especificação"
            disabled={isBusy}
            onClick={handleCancelEdit}
            className="rx-btn"
          >
            <X size={11} aria-hidden="true" />
            Cancelar edição
          </button>
        )}

        {/* Aceitar */}
        <button
          type="button"
          data-testid="spec-accept-btn"
          aria-label="Aceitar especificação"
          disabled={isBusy}
          onClick={handleAccept}
          className="rx-btn rx-btn-primary"
        >
          <Check size={11} aria-hidden="true" />
          Aceitar
        </button>
      </div>
    </section>
  );
}
